import { RootPage, RootPageTransparentPaper } from "@app/components/RootPage";
import { RootPageHeader } from "@app/components/RootPageHeader";
import { usePortfolioGroupParams } from "@app/hooks/usePortfolioGroupParams";
import { editPortfolioPages } from "@app/const/route.const";
import { usePortfolioGroupInfo } from "@app/pages/assetAllocation/usePortfolioGroupInfo";
import { useOptimizationJobsPuller } from "@app/pages/assetAllocation/editPortfolioGroup/useOptimizationJobsPuller";
import { EditPortfolioGroupAssetClassSelection } from "@app/pages/assetAllocation/editPortfolioGroup/EditPortfolioGroupAssetClassSelection";
import { EditPortfolioGroupEditCompare } from "@app/pages/assetAllocation/editPortfolioGroup/EditPortfolioGroupEditCompare";
import { EditPortfolioGroupCreateAllocations } from "@app/pages/assetAllocation/editPortfolioGroup/EditPortfolioGroupCreateAllocations";
import { EditPortfolioGroupAnalyze } from "@app/pages/assetAllocation/editPortfolioGroup/EditPortfolioGroupAnalyze";
import { EditPortfolioGroupOptimization } from "@app/pages/assetAllocation/editPortfolioGroup/EditPortfolioGroupOptimization";
import { SourceDataAsOf } from "@app/pages/assetAllocation/SourceDataAsOf";
import { CarModuleSectionCode } from "@app/hooks/useUserAccess";
import { AccessRouteControl } from "@app/components/AccessRouteControl";
import { Box } from "@mui/material";
import { CarNavigateButton } from "@app/components/NavigateButton";
import { ReactElement } from "react";

export const EditPortfolioPage = () => {
  const { portfolioGroupId } = usePortfolioGroupParams();
  const groupInfo = usePortfolioGroupInfo({ portfolioGroupId });
  const { portfolioGroupVersionId } = groupInfo;

  useOptimizationJobsPuller(portfolioGroupVersionId);

  const withNextButton = (page: ReactElement) => (nextPageUrl: string) => (
    <>
      {page}
      <Box
        sx={{
          mt: "auto",
          display: "flex",
          gap: 2.5,
          justifyContent: "center",
        }}
      >
        <CarNavigateButton
          sx={{ mt: 5, fontSize: "18px" }}
          url={`..\\${nextPageUrl}`}
        >
          Next
        </CarNavigateButton>
      </Box>
    </>
  );

  return (
    <RootPage sx={{ backgroundColor: "white", paddingBottom: 0 }}>
      <RootPageHeader
        isSmall
        title={`Portfolio Analysis - ${groupInfo.title}`}
        isLoading={groupInfo.isLoading}
        items={[
          {
            label: "Asset Class Selection",
            url: editPortfolioPages.assetClassSelection,
            moduleSection:
              CarModuleSectionCode.PORTFOLIO_ANALYSIS_ASSET_CLASS_SELECTION,
          },
          {
            label: "Create Model",
            url: editPortfolioPages.createModel,
            moduleSection: CarModuleSectionCode.PORTFOLIO_ANALYSIS_CREATE_MODEL,
          },
          {
            label: "Optimization",
            url: editPortfolioPages.optimization,
            hidden: !groupInfo.isPublished,
            moduleSection: CarModuleSectionCode.PORTFOLIO_ANALYSIS_OPTIMIZATION,
          },
          {
            label: "Edit/Compare",
            url: editPortfolioPages.editCompare,
            hidden: !groupInfo.isPublished,
            moduleSection: CarModuleSectionCode.PORTFOLIO_ANALYSIS_EDIT_COMPARE,
          },
          {
            label: "Analyze",
            url: editPortfolioPages.analyze,
            hidden: !groupInfo.isPublished,
            moduleSection: CarModuleSectionCode.PORTFOLIO_ANALYSIS_ANALYZE,
          },
        ]}
      />
      <RootPageTransparentPaper>
        <SourceDataAsOf />
        <AccessRouteControl
          items={[
            {
              path: editPortfolioPages.assetClassSelection,
              element: withNextButton(
                <EditPortfolioGroupAssetClassSelection />,
              )(editPortfolioPages.createModel),
              moduleSection:
                CarModuleSectionCode.PORTFOLIO_ANALYSIS_ASSET_CLASS_SELECTION,
            },
            {
              path: editPortfolioPages.createModel,
              element: groupInfo.isPublished ? (
                withNextButton(<EditPortfolioGroupCreateAllocations />)(
                  editPortfolioPages.optimization,
                )
              ) : (
                <EditPortfolioGroupCreateAllocations />
              ),
              moduleSection:
                CarModuleSectionCode.PORTFOLIO_ANALYSIS_CREATE_MODEL,
            },
            {
              path: editPortfolioPages.optimization,
              element: <EditPortfolioGroupOptimization />,
              moduleSection:
                CarModuleSectionCode.PORTFOLIO_ANALYSIS_OPTIMIZATION,
            },
            {
              path: editPortfolioPages.editCompare,
              element: withNextButton(<EditPortfolioGroupEditCompare />)(
                editPortfolioPages.analyze,
              ),
              moduleSection:
                CarModuleSectionCode.PORTFOLIO_ANALYSIS_EDIT_COMPARE,
            },
            {
              path: editPortfolioPages.analyze,
              element: <EditPortfolioGroupAnalyze />,
              moduleSection: CarModuleSectionCode.PORTFOLIO_ANALYSIS_ANALYZE,
            },
          ]}
          defaultPath={editPortfolioPages.assetClassSelection}
        />
      </RootPageTransparentPaper>
    </RootPage>
  );
};
